import React, { useState, useCallback } from 'react'
import { X, Upload, FileText, AlertCircle, CheckCircle } from 'lucide-react'
import { adminApi } from '../services/api'
import { QualityTier, WorkflowUploadRequest } from '../types'
import FileUpload from './FileUpload'
import toast from 'react-hot-toast'

interface WorkflowUploadProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  defaultQualityTier?: string
}

export default function WorkflowUpload({ isOpen, onClose, onSuccess, defaultQualityTier }: WorkflowUploadProps) {
  const [formData, setFormData] = useState({
    quality_tier: defaultQualityTier || QualityTier.LOW,
    name: '',
    description: '',
    workflow_json: null as Record<string, any> | null
  })
  const [uploading, setUploading] = useState(false)
  const [validationResult, setValidationResult] = useState<any>(null)
  const [validating, setValidating] = useState(false)

  const handleFileUpload = useCallback((file: File, content: Record<string, any>) => {
    setFormData(prev => ({
      ...prev,
      workflow_json: content,
      name: prev.name || file.name.replace('.json', '')
    }))
    
    // Auto-validate the workflow
    validateWorkflow(content)
  }, [])

  const validateWorkflow = async (workflowJson: Record<string, any>) => {
    try {
      setValidating(true)
      const response = await adminApi.validateWorkflowJson(workflowJson)
      setValidationResult(response.data)
    } catch (error) {
      console.error('Validation error:', error)
      setValidationResult({ valid: false, error: 'Validation failed' })
    } finally {
      setValidating(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.workflow_json) {
      toast.error('Please upload a workflow file')
      return
    }

    if (!formData.name.trim()) {
      toast.error('Please enter a workflow name')
      return
    }

    try {
      setUploading(true)
      const request: WorkflowUploadRequest = {
        quality_tier: formData.quality_tier,
        name: formData.name.trim(),
        description: formData.description.trim(),
        workflow_json: formData.workflow_json
      }

      const response = await adminApi.uploadWorkflow(request)
      
      if (response.success) {
        toast.success('Workflow uploaded successfully')
        onSuccess()
        onClose()
        resetForm()
      } else {
        toast.error(response.message || 'Failed to upload workflow')
      }
    } catch (error: any) {
      console.error('Upload error:', error)
      toast.error(error.response?.data?.detail || 'Failed to upload workflow')
    } finally {
      setUploading(false)
    }
  }

  const resetForm = () => {
    setFormData({
      quality_tier: defaultQualityTier || QualityTier.LOW,
      name: '',
      description: '',
      workflow_json: null
    })
    setValidationResult(null)
  }

  const handleClose = () => {
    if (!uploading) {
      resetForm()
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleClose} />

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-gray-900">Upload New Workflow</h3>
              <button
                onClick={handleClose}
                disabled={uploading}
                className="text-gray-400 hover:text-gray-500 disabled:opacity-50"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Quality Tier Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Quality Tier
                </label>
                <select
                  value={formData.quality_tier}
                  onChange={(e) => setFormData(prev => ({ ...prev, quality_tier: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  disabled={uploading}
                >
                  {Object.values(QualityTier).filter(tier => tier !== QualityTier.TESTING).map(tier => (
                    <option key={tier} value={tier}>
                      {tier.charAt(0).toUpperCase() + tier.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Workflow Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Workflow Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Enter workflow name"
                  disabled={uploading}
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Enter workflow description"
                  disabled={uploading}
                />
              </div>

              {/* File Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Workflow File *
                </label>
                <FileUpload
                  onFileUpload={handleFileUpload}
                  accept=".json"
                  disabled={uploading}
                />
              </div>

              {/* Validation Results */}
              {validating && (
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                  <span>Validating workflow...</span>
                </div>
              )}

              {validationResult && (
                <div className={`p-4 rounded-md ${validationResult.valid ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  <div className="flex items-start space-x-2">
                    {validationResult.valid ? (
                      <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <h4 className={`text-sm font-medium ${validationResult.valid ? 'text-green-800' : 'text-red-800'}`}>
                        {validationResult.valid ? 'Workflow Valid' : 'Workflow Invalid'}
                      </h4>
                      {validationResult.message && (
                        <p className={`text-sm mt-1 ${validationResult.valid ? 'text-green-700' : 'text-red-700'}`}>
                          {validationResult.message}
                        </p>
                      )}
                      {validationResult.details && (
                        <div className="mt-2">
                          <p className={`text-xs ${validationResult.valid ? 'text-green-600' : 'text-red-600'}`}>
                            Format: {validationResult.details.format}
                          </p>
                          {validationResult.details.node_count && (
                            <p className={`text-xs ${validationResult.valid ? 'text-green-600' : 'text-red-600'}`}>
                              Nodes: {validationResult.details.node_count}
                            </p>
                          )}
                          {validationResult.details.placeholders && validationResult.details.placeholders.length > 0 && (
                            <div className="mt-1">
                              <p className={`text-xs ${validationResult.valid ? 'text-green-600' : 'text-red-600'}`}>
                                Placeholders found: {validationResult.details.placeholders.join(', ')}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                      {validationResult.errors && validationResult.errors.length > 0 && (
                        <ul className="mt-2 text-xs text-red-600 list-disc list-inside">
                          {validationResult.errors.map((error: string, index: number) => (
                            <li key={index}>{error}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Submit Buttons */}
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={uploading}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading || !formData.workflow_json || (validationResult && !validationResult.valid)}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2 inline-block" />
                      Upload Workflow
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}